import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, ArrowLeft, Funnel } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { getWcPlayers } from "../lib/api";

const POSITION_FILTERS = ["ALL", "GKP", "DEF", "MID", "FWD"];

export default function WorldCupPlayers() {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pos, setPos] = useState("ALL");
    const [onlyTop50, setOnlyTop50] = useState(true);

    useEffect(() => {
        setLoading(true);
        getWcPlayers({ pl_only: true, limit: 2000 })
            .then((d) => setPlayers(d.players || []))
            .catch(() => toast.error("Failed to load PL crossref players"))
            .finally(() => setLoading(false));
    }, []);

    const visible = useMemo(() => {
        let list = players;
        if (pos !== "ALL") list = list.filter((p) => p.position === pos);
        if (onlyTop50) list = list.filter((p) => p.pl_value?.value_rank != null);
        return list.sort((a, b) => {
            const ar = a.pl_value?.value_rank ?? 999;
            const br = b.pl_value?.value_rank ?? 999;
            return ar - br;
        });
    }, [players, pos, onlyTop50]);

    return (
        <AppShell>
            <section className="space-y-5">
                <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                </Link>

                <header className="border-2 border-fa-mint/40 bg-[#0d0d0d] p-6" data-testid="wc-players-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Crown size={11} weight="fill" />
                        PL value picks at the World Cup
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
                        Who's hot in the Premier League right now?
                    </h1>
                    <p className="font-mono text-[12px] text-white/60 mt-3 leading-relaxed">
                        Cross-references each WC squad against current FPL 2025/26 data. Ranking uses{" "}
                        <strong className="text-fa-mint">median-trimmed points-per-million</strong> (excludes one-game freaks + cameo specialists). Top-50 only.
                    </p>
                </header>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap" data-testid="wc-players-filters">
                    <Funnel size={12} weight="bold" className="text-white/40" />
                    {POSITION_FILTERS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPos(p)}
                            data-testid={`filter-pos-${p}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${
                                pos === p
                                    ? "bg-fa-yellow text-black border-fa-yellow"
                                    : "border-white/15 text-white/60 hover:text-white"
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                    <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/60 cursor-pointer ml-auto">
                        <input
                            type="checkbox"
                            checked={onlyTop50}
                            onChange={(e) => setOnlyTop50(e.target.checked)}
                            data-testid="filter-top50"
                            className="accent-fa-mint"
                        />
                        Top-50 only
                    </label>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="font-mono text-[12px] text-white/40">Loading PL crossref…</div>
                ) : (
                    <div className="border border-white/10 bg-[#0d0d0d]" data-testid="wc-players-table">
                        <table className="w-full font-mono text-[11px]">
                            <thead>
                                <tr className="text-white/40 uppercase tracking-widest text-[9px]">
                                    <th className="text-left p-2">Rank</th>
                                    <th className="text-left p-2">Player</th>
                                    <th className="text-left p-2">WC Team</th>
                                    <th className="text-left p-2">Pos</th>
                                    <th className="text-left p-2 hidden md:table-cell">PL Team</th>
                                    <th className="text-right p-2">PPM</th>
                                    <th className="text-right p-2 hidden md:table-cell">Pts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((p, i) => {
                                    const pv = p.pl_value || {};
                                    return (
                                        <tr
                                            key={p.fd_player_id || i}
                                            className="border-t border-white/5 hover:bg-white/5"
                                            data-testid={`wc-row-${p.fd_player_id}`}
                                        >
                                            <td className="p-2 text-fa-yellow font-display font-black tabular-nums">
                                                {pv.value_rank ? `#${pv.value_rank}` : "—"}
                                            </td>
                                            <td className="p-2 text-white font-display font-black">{p.name}</td>
                                            <td className="p-2 text-white/60">
                                                {p.team_short ? (
                                                    <Link to={`/worldcup/team/${p.team_id}`} className="hover:text-white">
                                                        {p.team_short}
                                                    </Link>
                                                ) : "—"}
                                            </td>
                                            <td className="p-2 text-white/60">{p.position}</td>
                                            <td className="p-2 text-white/60 hidden md:table-cell">{pv.fpl_team_name || "—"}</td>
                                            <td className="p-2 text-fa-mint text-right tabular-nums font-display font-black">
                                                {pv.ppm != null ? pv.ppm.toFixed(2) : "—"}
                                            </td>
                                            <td className="p-2 text-white/60 text-right tabular-nums hidden md:table-cell">
                                                {pv.total_points ?? "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="p-2 font-mono text-[10px] text-white/30 border-t border-white/5">
                            Showing {visible.length} players
                        </div>
                    </div>
                )}
            </section>
        </AppShell>
    );
}
